/** @jsxImportSource @emotion/react */

import { MissionManager } from "@a_team/models/dist/MissionObject";
import { RegisteredUserObject } from "@a_team/models/dist/UserObject";
import { Checkbox, Typography } from "@a_team/ui-components";
import { CSSObject } from "@emotion/react";
import { CollaboratorState } from "components/Collaborators/CollaboratorTable";
import { RoleActionForCollaborator } from "components/Collaborators/CollaboratorTable/RoleActionForCollaborator";
import { RoleSelectorForCollaborator } from "components/Collaborators/CollaboratorTable/RoleSelectorForCollaborator";
import {
  CollaboratorTableCellOptionProps,
  TableCellOption,
} from "components/Collaborators/CollaboratorTable/TableCellOption";
import { isBillingContact } from "views/utils";
import { NameAndAvatar } from "components/Collaborators/NameAndAvatar";
import { CustomSelect } from "components/Collaborators/RoleSelector/CustomSelect";
import { Flags } from "configs/featureFlags";
import { getLabelForRole, Role } from "configs/role";
import useUserRoleFromAccount from "hooks/access/useUserRoleFromAccount";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { observer } from "mobx-react-lite";
import { AccountMember, AccountRoleAccessLevel } from "models/Account";
import { EmailSettings } from "models/Mission";
import { useUpdateMemberAttrs } from "queries/accounts/collaborators/useUpdateMemberAttrs";
import React, { FC, Fragment, useMemo } from "react";
import { useRootStore } from "store";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "hooks/useCSSRulesWithTheme";
import { useHistory } from "react-router-dom";
import { ClientSettingsWorkspaceLocation } from "Locations";
import { TEAM_SUMMARY_UPDATES_HASH } from "components/Collaborators/constants";

const getCSSRules: CSSRulesResolver = ({ colors }) => ({
  link: {
    color: colors.Hannibal[600],
    cursor: "pointer",
  },
});

interface CollaboratorRowProps {
  collaborators: Array<AccountMember>;
  formState: CollaboratorState[];
  updateMissionSettings: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEmailSettingsChange: (
    collaborator: AccountMember,
    emailSettings: string[]
  ) => void;
  showAdminSettings: boolean;
  showEmailSettings: boolean;
  saving: boolean;
  managers: MissionManager[];
  style: CSSObject;
  currentUser?: RegisteredUserObject;
  closeCollaboratorManageModal?: () => void;
}

export const CollaboratorRows: FC<CollaboratorRowProps> = observer(
  ({
    showAdminSettings,
    showEmailSettings,
    saving,
    // managers,
    collaborators,
    formState,
    updateMissionSettings,
    handleEmailSettingsChange,
    currentUser,
    style,
    closeCollaboratorManageModal,
  }) => {
    const styles = useCSSRulesWithTheme(getCSSRules);
    const history = useHistory();
    const { missionSpecStore } = useRootStore();
    const missionSpecId = missionSpecStore?.missionSpec?.id;
    const isMissionContext = !!missionSpecId;

    const { flagEnabled: clientRolesAndPermissionsEnabled } = useFeatureFlag(
      Flags.ClientRolesAndPerms
    );
    const { flagEnabled: builderRatingsEnabled } = useFeatureFlag(
      Flags.BuilderRatings
    );
    const { flagEnabled: teamSummaryEmailEnabled } = useFeatureFlag(
      Flags.TeamSummaryEmail
    );
    const { flagEnabled: hideTeamPulseEmailSettingEnabled } = useFeatureFlag(
      Flags.HideTeamPulseEmailSettingEnabled
    );
    const currentUserRole = useUserRoleFromAccount({});
    const { mutate: updateAttrs } = useUpdateMemberAttrs();

    const handleMissionUpdatesClick = () => {
      closeCollaboratorManageModal && closeCollaboratorManageModal();
      history.push(
        `${ClientSettingsWorkspaceLocation}${TEAM_SUMMARY_UPDATES_HASH}`
      );
    };

    const prettyAccessLevel = (collaborator: AccountMember): string => {
      const isAdmin = collaborator?.user?.isAdmin;
      const accessLevel = collaborator.accessLevel;
      const roles = collaborator.roles || [];
      const hasTeamAdvisorRole = roles.includes(Role.TeamAdvisor);
      const userHasTAAccessLevel = [
        AccountRoleAccessLevel.BDAdmin,
        AccountRoleAccessLevel.BDManager,
      ].includes(accessLevel);
      const userIsAdminWithTAAccessLevel = isAdmin && userHasTAAccessLevel;

      // In some rare cases an A.Team admin will not have the Team Advisor role
      const isAdminButNotTeamAdvisor =
        isAdmin && !hasTeamAdvisorRole && roles.length === 1;

      if (!isAdminButNotTeamAdvisor && userIsAdminWithTAAccessLevel) {
        return getLabelForRole(Role.TeamAdvisor);
      }

      if (
        isAdminButNotTeamAdvisor ||
        accessLevel === AccountRoleAccessLevel.ClientAdmin
      ) {
        return "Admin";
      }

      return "Team Member";
    };

    const renderedCollaborators = useMemo(() => {
      return collaborators.map((collaborator: AccountMember) => {
        const attributes = collaborator.attributes;
        const userId = collaborator.user.id;
        const userIsAdmin = !!collaborator.user?.isAdmin;
        const accessLevel = prettyAccessLevel(collaborator);
        const acceptedInvite = collaborator.user.activated;
        const settings = formState.find(
          (s: { userId: any }) => s.userId === userId
        );

        const tableCellProps: Omit<CollaboratorTableCellOptionProps, "name"> = {
          style,
          userIsAdmin,
          userId,
          settings,
          onChange: updateMissionSettings,
          disabled: saving,
        };

        const userIsMissionMember = collaborator.roles?.includes(
          `${Role.MissionMember}:${missionSpecId}` as string as Role
        );

        const hasMissionWriteAccess = [
          Role.TeamAdvisor,
          Role.WorkspaceAdmin,
          `${Role.MissionAdmin}:${missionSpecId}` as string as Role,
        ].some((role) => collaborator.roles?.includes(role));

        return {
          userId,
          collaborator,
          acceptedInvite,
          accessLevel,
          tableCellProps,
          attributes,
          userIsMissionMember,
          hasMissionWriteAccess,
        };
      });
    }, [collaborators, formState, saving, style]);

    return (
      <>
        {renderedCollaborators.map(
          ({
            userId,
            collaborator,
            acceptedInvite,
            accessLevel,
            tableCellProps,
            userIsMissionMember,
            hasMissionWriteAccess,
          }) => {
            // We don't want to send the builder feedback or mission updates to billing contacts
            const isUserBillingContact = isBillingContact({
              email: collaborator.user.email,
              firstName: collaborator.user.firstName,
              lastName: collaborator.user.lastName,
            });

            const state = formState.find(
              (s) => s.userId === collaborator.user.id
            );
            const emailSettings: EmailSettings[] = [];
            if (state?.invoices) {
              emailSettings.push(EmailSettings.Invoices);
            }
            if (state?.teamPulse) {
              emailSettings.push(EmailSettings.TeamPulse);
            }
            if (!isUserBillingContact && state?.builderFeedback) {
              emailSettings.push(EmailSettings.BuilderFeedback);
            }
            if (
              teamSummaryEmailEnabled &&
              !isUserBillingContact &&
              state?.missionUpdates
            ) {
              emailSettings.push(EmailSettings.MissionUpdates);
            }

            return (
              <Fragment key={userId}>
                <div css={style}>
                  <NameAndAvatar
                    {...{
                      fullName: collaborator.user.fullName,
                      profilePictureURL: collaborator.user.profilePictureURL,
                      email: collaborator.user.email,
                      acceptedInvite,
                      avatarSize: "lg",
                      indicateIsSelf: currentUser?.uid === collaborator.user.id,
                    }}
                  />
                </div>
                <div css={style}>
                  {clientRolesAndPermissionsEnabled &&
                  accessLevel !== getLabelForRole(Role.TeamAdvisor) &&
                  currentUserRole !== Role.MissionMember ? (
                    <RoleSelectorForCollaborator collaborator={collaborator} />
                  ) : (
                    <Typography
                      variant="textMedium"
                      weight="regular"
                      component="p"
                    >
                      {accessLevel}
                    </Typography>
                  )}
                </div>

                {showAdminSettings &&
                  !clientRolesAndPermissionsEnabled &&
                  ["Team Pulse", "Invoices", "Main Manager"].map((name) => (
                    <TableCellOption
                      key={name}
                      name={name}
                      {...tableCellProps}
                    />
                  ))}

                {showAdminSettings && clientRolesAndPermissionsEnabled && (
                  <div css={[style, { ...style, justifyContent: "center" }]}>
                    {!userIsMissionMember && (
                      <Checkbox
                        checked={!!collaborator?.attributes?.hiringManager}
                        onChange={(e) =>
                          updateAttrs({
                            userId,
                            attributes: {
                              hiringManager: e.target.checked ? true : null,
                            },
                            prevState: collaborators,
                          })
                        }
                      />
                    )}
                  </div>
                )}
                {showEmailSettings && (
                  <div css={style}>
                    {currentUserRole === Role.TeamAdvisor ||
                    currentUser?.uid === collaborator.user.id ? (
                      <CustomSelect
                        multiple={true}
                        disabled={saving}
                        options={[
                          ...(!hideTeamPulseEmailSettingEnabled
                            ? [
                                {
                                  label: "Team Pulse",
                                  value: EmailSettings.TeamPulse,
                                  description:
                                    "Receive weekly updates about your team.",
                                },
                              ]
                            : []),
                          ...(hasMissionWriteAccess
                            ? [
                                {
                                  label: "Invoices",
                                  value: EmailSettings.Invoices,
                                  description:
                                    "Receive invoices for your missions.",
                                },
                              ]
                            : []),
                          ...(builderRatingsEnabled
                            ? [
                                {
                                  label: "Builder feedback",
                                  value: EmailSettings.BuilderFeedback,
                                  description:
                                    "The collaborator will receive feedback requests for team members who have finished their work on the mission.",
                                  disabled: isUserBillingContact,
                                },
                              ]
                            : []),
                          {
                            label: "Team summaries",
                            value: EmailSettings.MissionUpdates,
                            description: teamSummaryEmailEnabled ? (
                              "Receive team updates to your inbox."
                            ) : (
                              <>
                                Summaries are disabled.{" "}
                                <span
                                  css={styles.link}
                                  onClick={handleMissionUpdatesClick}
                                >
                                  Click here
                                </span>{" "}
                                to reenable for your workspace and get insights
                                on team progress.
                              </>
                            ),
                            disabled:
                              isUserBillingContact || !teamSummaryEmailEnabled,
                          },
                        ]}
                        value={emailSettings}
                        title={"Email settings"}
                        onChange={(emailSettings) => {
                          if (Array.isArray(emailSettings)) {
                            handleEmailSettingsChange(
                              collaborator,
                              emailSettings
                            );
                          }
                        }}
                      />
                    ) : (
                      <Typography
                        variant="textMedium"
                        weight="regular"
                        component="p"
                        color="Grey@400"
                      >
                        No access
                      </Typography>
                    )}
                  </div>
                )}

                <div css={style}>
                  <RoleActionForCollaborator
                    collaborator={collaborator}
                    currentUser={currentUser}
                    isMissionContext={!!isMissionContext}
                  />
                </div>
              </Fragment>
            );
          }
        )}
      </>
    );
  }
);
